import * as React from "react";
import { useEffect, useState } from "react";
import { 
  Grid, 
  Box, 
  Tab, 
  TextField, 
  Paper, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Toolbar,
  CircularProgress
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import HttpClient from 'src/providers/HttpClient';
import ApiResources from 'src/providers/ApiResources';
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { useDataProvider, useTranslate, useNotify } from "react-admin";
import { Edit, FormTab, TabbedForm } from "src/enterprise/ra-rbac";
import { useDefineAppLocation } from 'src/enterprise/ra-navigation';
import { FileInputAdornmentBasic } from "src/components/file-input";
import { ConfigurationToolbar } from 'src/resources/configuration';
import { InputHelper } from 'src/components/input';

const TypeField = (props) => {
  const translate = useTranslate();
  const {type, required, name, label, options, value, onChange, ...rest} = props;
  let component = null;

  switch (type) {
    case "select": {
      component = (
        <FormControl fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            id={name}
            value={value ?? ""}
            size="small"
            onChange={(e) =>
              onChange(e.target.value, name)
            }
            {...rest}
          >
            {options.map((option: any, optionIndex: number) => (
              <MenuItem
                key={optionIndex}
                value={option}
              >
                {translate(`configuration.field.option.${option}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
      break;
    }
    case "multiselect": {
      component = (
        <FormControl fullWidth>
          <InputLabel>{label}</InputLabel>
          <Select
            multiple
            id={name}
            value={value && !Array.isArray(value) ? value.split(",") : value ?? []}
            label={label}
            size="small"
            onChange={(e) =>
              onChange(e.target.value, name)
            }
            sx={{
              '& .MuiSelect-multiple': {
                whiteSpace: 'normal !important',
              },
            }}
            {...rest}
          >
            {options.map((option: any, optionIndex: number) => (
              <MenuItem
                key={optionIndex}
                value={option}
              >
                {translate(`configuration.field.option.${option}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
      break;
    }
    case "number": {
      component = (
        <TextField 
          name={name}
          value={value ?? ""}
          label={label}
          required={required}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          size="small"
          onChange={(e) =>
            onChange(e.target.value, name)
          }
          {...rest}
        />
      );
      break;
    }
    case "file": {
      component = (
        <FileInputAdornmentBasic
            name={name}
            value={value ?? ""}
            label={label}
            required={required}
            fileTypes={["JPG", "PNG", "GIF", "PDF", "MP4"]}
            path="admin/media/common"
            multiple={false}
            includeDomain={true}
            onChange={onChange}
            {...rest}
        />
      );
      break;
    }
    default: {
      component = (
        <TextField 
          name={name}
          value={value ?? ""}
          label={label}
          required={required}
          size="small"
          onChange={(e) =>
            onChange(e.target.value, name)
          }
          {...rest}
        />
      );
      break;
    }
  }

  return component;
};

export const Configuration = () => {
  useDefineAppLocation('admin/configurations');
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const notify = useNotify();

  const [tabValue, setTabValue] = useState('0');
  const [items, setItems] = useState([]);
  const [data, setData] = useState({});
  const [configurationSaving, setConfigurationSaving] = useState(false);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    getConfigurationItems();
  }, []);

  const handleUpdate = (value, name) => {
    let valueFormatted = Array.isArray(value) ? value.toString() : value;
    
    setData({ ...data, [`${name}`]: valueFormatted });
  };
  
  const handleSave = async (event) => {
    event.preventDefault();   
  
    setConfigurationSaving(true);
  
    await HttpClient(ApiResources.getConfigurations(), {
        method: 'PUT',
        body: JSON.stringify({configurations: data})
    }).then(({ json }) => {
        notify(`Configuration Updated`);
        setConfigurationSaving(false);
        return;
    }).catch(error => {
      notify(error.message, { type: 'error' });
    });
  }

  const getConfigurationItems = (path?: string) => {
    HttpClient(ApiResources.getConfigurations(), {
      method: "GET",
    }).then(({ json }) => {
      setItems(json);
    }).catch(error => {
      notify(error.message, { type: 'error' });
    });
  }

  return (
    <>
    <ConfigurationToolbar />
    <Box sx={{ width: "100%", typography: "body1"}}>
      <Paper>
        <TabContext value={tabValue}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList onChange={handleTabChange}>
              {Object.keys(items).map((panelCode: any, panelIndex: number) => {
                return (
                  <Tab key={panelCode} label={panelCode} value={panelIndex.toString()} />
                );
              })}
            </TabList>
          </Box>

          {Object.keys(items).map((panelCode: any, panelIndex: number) => {
            return (
              <TabPanel key={panelCode} value={panelIndex.toString()}>
                {Object.keys(items[panelCode]).map((groupCode: any, groupIndex: number) => {
                  return (
                    <Accordion key={groupIndex}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                      >
                        <Typography>{translate(`configuration.${groupCode}`)}</Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{paddingTop: "15px"}}>
                        <Grid container item md={12} sm={12} rowSpacing={1} columnSpacing={2}>
                          {Object.keys(items[panelCode][groupCode]).map((fieldName: any, fieldIndex: number) => {
                            let field = items[panelCode][groupCode][fieldName];

                            return (
                              <Grid item sm={12} md={6} key={fieldIndex} sx={{marginBottom: "18px"}}>
                                <TypeField 
                                  type={field.type} 
                                  required={field?.required ?? false} 
                                  name={field.code} 
                                  label={translate(`configuration.field.${fieldName}`)} 
                                  options={field?.options ?? false} 
                                  value={data[field.code] ?? field?.value}
                                  onChange={handleUpdate} 
                                />
                                { field.helper && 
                                <p style={{margin: "4px 14px 0px 14px"}}>
                                  <InputHelper text={field.helper}/>
                                </p>
                                }
                              </Grid>            
                            );
                          })}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>                    
                  );
                })}

              </TabPanel>
            );
          })}
        </TabContext>
        <Toolbar sx={{ backgroundColor: "#F3F4F6" }}>
            <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
              <Grid item>
                <Button variant="contained" onClick={handleSave}>
                  {configurationSaving ? <CircularProgress size={18} thickness={2} sx={{ fontSize: "18px", color: "#ffffff"}}/> : <SaveIcon sx={{ fontSize: "18px"}} />} 
                  <Typography component="span" sx={{ fontSize: "14px",marginLeft: "8px", fontWeight: 600}}>Save</Typography>
                </Button>
              </Grid>
            </Grid>
        </Toolbar>
      </Paper>
    </Box>
    </>
  );
};
